import React, { useEffect, useState } from 'react';
import { Input, Button, Modal, Select, Tag, Empty, Spin, Avatar, Divider, Tooltip } from 'antd';
import { useHistory } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import {
    LIST_ALL_USER,
    USER_ROLE,
    GET_SUPERIOR_ROLES,
    GET_USERS_BY_ROLE,
    UPDATE_USER_ROLE_PARENT
} from './../../../Utils/appConstants';
import { getRequest, postRequest } from './../../../Utils/AppApi';
import { getUser, kycStatusValue, INRFormat } from './../../../Utils/Common';
import './UpgradeUserRole.css';

function UpgradeUserRole() {
    const user    = getUser();
    const history = useHistory();

    const [searchInput, setSearchInput]     = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching]         = useState(false);
    const [hasSearched, setHasSearched]     = useState(false);

    const [isModalRoleParent, setIsModalRoleParent]     = useState(false);
    const [selectedUserForRole, setSelectedUserForRole] = useState(null);
    const [allRoles, setAllRoles]                       = useState([]);
    const [newRoleId, setNewRoleId]                     = useState(null);
    const [parentRoleId, setParentRoleId]               = useState(null);
    const [parentUserId, setParentUserId]               = useState(null);
    const [parentRoleOptions, setParentRoleOptions]     = useState([]);
    const [parentUserOptions, setParentUserOptions]     = useState([]);
    const [roleParentLoading, setRoleParentLoading]     = useState(false);

    useEffect(() => {
        getRequest(USER_ROLE, history).then((response) => {
            if (response.success === true) setAllRoles(response.data || []);
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = async () => {
        const q = (searchInput || '').trim();
        if (!q) return toast.error("Please enter a username to search");

        setSearching(true);
        setHasSearched(true);
        try {
            const url = `${LIST_ALL_USER}?page=1&limit=10&roleId=all&search=${encodeURIComponent(q)}`;
            const res = await getRequest(url, history);
            if (res.success === true) {
                const docs = res.data?.docs || [];
                setSearchResults(docs);
                if (!docs.length) toast.info("No user found for that username");
            } else {
                setSearchResults([]);
                toast.error(res.message || "Search failed");
            }
        } catch {
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

    const handleClear = () => {
        setSearchInput("");
        setSearchResults([]);
        setHasSearched(false);
    };

    const loadParentRoles = async (roleId) => {
        if (!roleId) { setParentRoleOptions([]); return; }
        setRoleParentLoading(true);
        try {
            const res = await getRequest(`${GET_SUPERIOR_ROLES}/${roleId}`, history);
            setParentRoleOptions(res.success ? (res.data || []) : []);
        } catch { setParentRoleOptions([]); }
        finally { setRoleParentLoading(false); }
    };

    const loadParentUsers = async (roleId, excludeUserId) => {
        if (!roleId) { setParentUserOptions([]); return; }
        setRoleParentLoading(true);
        try {
            const res = await getRequest(
                `${GET_USERS_BY_ROLE}?roleId=${roleId}&excludeUserId=${excludeUserId || ''}`,
                history
            );
            setParentUserOptions(res.success ? (res.data || []) : []);
        } catch { setParentUserOptions([]); }
        finally { setRoleParentLoading(false); }
    };

    const handleOpenRoleParentModal = (row) => {
        setSelectedUserForRole(row);
        setIsModalRoleParent(true);
        setNewRoleId(null);
        setParentRoleId(null);
        setParentUserId(null);
        setParentRoleOptions([]);
        setParentUserOptions([]);
        setRoleParentLoading(false);
    };

    const handleRoleChange = async (value) => {
        setNewRoleId(value);
        setParentRoleId(null);
        setParentUserId(null);
        setParentUserOptions([]);
        await loadParentRoles(value);
    };

    const handleParentRoleChange = async (value) => {
        setParentRoleId(value);
        setParentUserId(null);
        await loadParentUsers(value, selectedUserForRole?.userId);
    };

    const handleSaveRoleParent = async () => {
        if (!newRoleId)    return toast.error("Please select a role");
        if (!parentRoleId) return toast.error("Please select a parent role");
        if (!parentUserId) return toast.error("Please select a parent user");

        const currentOrder = getCurrentRoleOrder();
        const newRole      = allRoles.find(r => r._id === newRoleId);
        if (currentOrder != null && newRole && Number(newRole.order) >= currentOrder) {
            return toast.error("New role must be strictly more senior than the current role");
        }
        if (!parentRoleOptions.length) {
            return toast.error("No eligible parent role available for this role");
        }

        postRequest(UPDATE_USER_ROLE_PARENT, {
            userId:   selectedUserForRole.userId,
            roleId:   newRoleId,
            parentId: parentUserId
        }, history).then((response) => {
            if (response.success) {
                toast.success(response.message || "Updated successfully");
                setIsModalRoleParent(false);
                handleSearch();
            } else {
                toast.error(response.message || "Update failed");
            }
        });
    };

    const getCurrentRoleOrder = () => {
        if (!selectedUserForRole || !allRoles.length) return null;
        const currentId   = selectedUserForRole.roleObjectId || selectedUserForRole.user?.roleId;
        const currentCode = selectedUserForRole.role?.code;
        const matched =
            allRoles.find(r => String(r._id) === String(currentId)) ||
            allRoles.find(r => String(r.code) === String(currentCode));
        return matched ? Number(matched.order) : null;
    };

    const getEligibleNewRoles = () => {
        const currentOrder = getCurrentRoleOrder();
        if (currentOrder == null) return allRoles;
        return allRoles.filter(r => Number(r.order) < currentOrder);
    };

    const getPreviewFlags = () => {
        if (!newRoleId) return [];
        const r = allRoles.find(x => x._id === newRoleId);
        if (!r) return [];
        const code = String(r.code || '').toUpperCase();
        const flags = [];
        if (code === 'S') flags.push('STAFF');
        if (code === 'M') flags.push('MEMBER');
        return flags;
    };

    // --- Role color helper (badge color by role code) ---
    const roleColor = (code) => {
        const map = {
            'SA': '#7c3aed', 'A': '#dc2626', 'SH': '#0891b2',
            'SD': '#2563eb', 'MD': '#7c3aed', 'D': '#059669',
            'P': '#f59e0b', 'V': '#65a30d', 'S': '#4f46e5', 'M': '#db2777'
        };
        return map[String(code || '').toUpperCase()] || '#6b7280';
    };

    const kycBadge = (status) => {
        if (status === 3) return { label: 'KYC Verified', color: '#059669', bg: '#d1fae5' };
        if (status === 2) return { label: 'KYC Rejected', color: '#dc2626', bg: '#fee2e2' };
        if (status === 1) return { label: 'KYC In Process', color: '#d97706', bg: '#fef3c7' };
        return { label: 'KYC Pending', color: '#6b7280', bg: '#f3f4f6' };
    };

    return (
        <div className="upgrade-role-page">
            {/* ---------- HERO HEADER ---------- */}
            <div className="ur-hero">
                <div className="ur-hero-inner">
                    <div className="ur-hero-text">
                        <div className="ur-hero-icon">
                            <i className="fa fa-sitemap"></i>
                        </div>
                        <div>
                            <h1 className="ur-hero-title">
                                Upgrade <span>User Role</span>
                            </h1>
                            <p className="ur-hero-subtitle">
                                Search a user by username, then upgrade their role and reassign their parent in one step.
                            </p>
                        </div>
                    </div>

                    {/* Search bar */}
                    <div className="ur-search-wrap">
                        <div className="ur-search-box">
                            <i className="fa fa-search ur-search-icon"></i>
                            <input
                                type="text"
                                className="ur-search-input"
                                placeholder="Enter username (e.g. SRPAY1011)"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            {searchInput && (
                                <i
                                    className="fa fa-times-circle ur-search-clear"
                                    onClick={() => setSearchInput("")}
                                    title="Clear"
                                ></i>
                            )}
                        </div>
                        <button
                            className="ur-btn ur-btn-primary"
                            onClick={handleSearch}
                            disabled={searching}
                        >
                            {searching ? (
                                <><i className="fa fa-spinner fa-spin mr-1"></i> Searching</>
                            ) : (
                                <><i className="fa fa-arrow-right mr-1"></i> Search</>
                            )}
                        </button>
                        {hasSearched && (
                            <button className="ur-btn ur-btn-ghost" onClick={handleClear}>
                                Reset
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ---------- CONTENT ---------- */}
            <div className="ur-content">
                {searching && (
                    <div className="ur-loading">
                        <Spin size="large" />
                        <p>Searching for user…</p>
                    </div>
                )}

                {!searching && hasSearched && !searchResults.length && (
                    <div className="ur-empty">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span>
                                    No user found for <b>"{searchInput}"</b>.
                                    <br />
                                    <small className="text-muted">Try a different username.</small>
                                </span>
                            }
                        />
                    </div>
                )}

                {!searching && searchResults.length > 0 && (
                    <div className="ur-results-head">
                        <span className="ur-results-count">
                            {searchResults.length} result{searchResults.length > 1 ? 's' : ''} found
                        </span>
                    </div>
                )}

                {!searching && searchResults.map((row) => {
                    const roleCode   = row.role?.code;
                    const rc         = roleColor(roleCode);
                    const initials   = (row.user?.name || '?')
                        .split(' ')
                        .map(w => w[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase();
                    const kyc        = kycBadge(row.user?.KYCStatus);
                    const walletAmt  = row.walletbalances?.balance || 0;

                    return (
                        <div key={row._id} className="ur-card">
                            {/* Left accent bar */}
                            <div className="ur-card-accent" style={{ background: rc }}></div>

                            <div className="ur-card-body">
                                {/* Header row: avatar + name + role pill */}
                                <div className="ur-card-head">
                                    <Avatar
                                        size={64}
                                        style={{
                                            background: `linear-gradient(135deg, ${rc}, ${rc}cc)`,
                                            fontSize: 22,
                                            fontWeight: 700,
                                            boxShadow: `0 6px 16px ${rc}55`
                                        }}
                                    >
                                        {initials}
                                    </Avatar>

                                    <div className="ur-card-name">
                                        <h3>{row.user?.name || '—'}</h3>
                                        <div className="ur-card-meta">
                                            <span className="ur-username">
                                                <i className="fa fa-id-badge mr-1"></i>
                                                {row.user?.username}
                                            </span>
                                            {row.user?.isStaff    && <Tag color="blue">STAFF</Tag>}
                                            {row.user?.isMember   && <Tag color="cyan">MEMBER</Tag>}
                                            {row.user?.isEmployee && <Tag color="orange">EMPLOYEE</Tag>}
                                        </div>
                                    </div>

                                    <div className="ur-role-pill" style={{ background: rc }}>
                                        {row.role?.name}
                                    </div>
                                </div>

                                <Divider className="ur-divider" />

                                {/* Stats grid */}
                                <div className="ur-stats">
                                    <div className="ur-stat">
                                        <i className="fa fa-user-friends ur-stat-icon" style={{ color: '#2563eb' }}></i>
                                        <div>
                                            <span className="ur-stat-label">Parent</span>
                                            <span className="ur-stat-value">
                                                {row.userParentDetail?.username || '—'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="ur-stat">
                                        <i className="fa fa-mobile-alt ur-stat-icon" style={{ color: '#7c3aed' }}></i>
                                        <div>
                                            <span className="ur-stat-label">Mobile</span>
                                            <span className="ur-stat-value">{row.mobile || '—'}</span>
                                        </div>
                                    </div>

                                    <div className="ur-stat">
                                        <i className="fa fa-wallet ur-stat-icon" style={{ color: '#059669' }}></i>
                                        <div>
                                            <span className="ur-stat-label">Wallet</span>
                                            <span className="ur-stat-value">₹ {INRFormat(walletAmt)}</span>
                                        </div>
                                    </div>

                                    <div className="ur-stat">
                                        <i className="fa fa-shield-alt ur-stat-icon" style={{ color: kyc.color }}></i>
                                        <div>
                                            <span className="ur-stat-label">KYC Status</span>
                                            <span className="ur-kyc-badge" style={{ color: kyc.color, background: kyc.bg }}>
                                                {kyc.label}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="ur-stat">
                                        <i
                                            className={`fa ${row.user?.status ? 'fa-check-circle' : 'fa-times-circle'} ur-stat-icon`}
                                            style={{ color: row.user?.status ? '#059669' : '#dc2626' }}
                                        ></i>
                                        <div>
                                            <span className="ur-stat-label">Account</span>
                                            <span
                                                className="ur-stat-value"
                                                style={{ color: row.user?.status ? '#059669' : '#dc2626' }}
                                            >
                                                {row.user?.status ? 'Active' : 'Deactivated'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="ur-stat">
                                        <i className="fa fa-building ur-stat-icon" style={{ color: '#d97706' }}></i>
                                        <div>
                                            <span className="ur-stat-label">Outlet</span>
                                            <span className="ur-stat-value">{row.outletName || '—'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action button */}
                            <div className="ur-card-action">
                                <Tooltip title={user.data.role.code !== 'A' ? 'Only Admin can change roles' : ''}>
                                    <button
                                        className="ur-btn ur-btn-upgrade"
                                        onClick={() => handleOpenRoleParentModal(row)}
                                        disabled={user.data.role.code !== 'A'}
                                    >
                                        <i className="fa fa-arrow-up mr-2"></i>
                                        Upgrade Role
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                    );
                })}

                {/* Initial empty state */}
                {!searching && !hasSearched && (
                    <div className="ur-initial-state">
                        <div className="ur-initial-icon">
                            <i className="fa fa-search"></i>
                        </div>
                        <h3>Search a user to begin</h3>
                        <p>Enter a username above and press Enter to find a user and upgrade their role.</p>
                    </div>
                )}
            </div>

            <ToastContainer
                position="top-center"
                autoClose={5000}
                limit={2}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />

            {/* ---------- Modal (polished) ---------- */}
            {isModalRoleParent && selectedUserForRole && (
                <Modal
                    title={
                        <div className="ur-modal-title">
                            <i className="fa fa-level-up-alt mr-2"></i>
                            Upgrade Role
                        </div>
                    }
                    width={640}
                    open={isModalRoleParent}
                    onOk={handleSaveRoleParent}
                    onCancel={() => setIsModalRoleParent(false)}
                    okText="Save Changes"
                    cancelText="Cancel"
                    confirmLoading={roleParentLoading}
                    okButtonProps={{
                        disabled: !newRoleId || !parentRoleId || !parentUserId || roleParentLoading,
                        className: 'ur-modal-ok'
                    }}
                >
                    {/* Current state summary */}
                    <div className="ur-modal-summary">
                        <div className="ur-modal-summary-row">
                            <Avatar
                                size={48}
                                style={{
                                    background: `linear-gradient(135deg, ${roleColor(selectedUserForRole.role?.code)}, ${roleColor(selectedUserForRole.role?.code)}cc)`,
                                    fontWeight: 700
                                }}
                            >
                                {(selectedUserForRole.user?.name || '?').slice(0, 2).toUpperCase()}
                            </Avatar>
                            <div>
                                <div className="ur-modal-summary-name">
                                    {selectedUserForRole.user?.name}
                                </div>
                                <div className="ur-modal-summary-meta">
                                    {selectedUserForRole.user?.username} &nbsp;•&nbsp;
                                    Current: <b>{selectedUserForRole.role?.name}</b> &nbsp;•&nbsp;
                                    Parent: <b>{selectedUserForRole.userParentDetail?.username || '—'}</b>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="ur-field">
                        <label className="ur-field-label">
                            New Role <span className="ur-required">*</span>
                            <small className="ur-field-hint">Strictly more senior than current</small>
                        </label>
                        <Select
                            className="w-100"
                            placeholder="Select New Role"
                            value={newRoleId || undefined}
                            onChange={handleRoleChange}
                            showSearch
                            allowClear
                            size="large"
                            optionFilterProp="label"
                            options={getEligibleNewRoles().map(r => ({
                                value: r._id,
                                label: `${r.name} (${r.code})`
                            }))}
                        />
                        {getEligibleNewRoles().length === 0 && (
                            <small className="ur-error-hint">
                                <i className="fa fa-exclamation-circle mr-1"></i>
                                No more senior role available to upgrade to.
                            </small>
                        )}
                        {newRoleId && (() => {
                            const flags = getPreviewFlags();
                            return (
                                <div className="ur-preview-flags">
                                    <span className="ur-preview-label">After save:</span>
                                    {flags.length ? flags.map(f => (
                                        <span key={f} className="ur-flag-pill">{f}</span>
                                    )) : (
                                        <span className="ur-preview-none">No special flags (regular user)</span>
                                    )}
                                </div>
                            );
                        })()}
                    </div>

                    <div className="ur-field">
                        <label className="ur-field-label">
                            Parent Role <span className="ur-required">*</span>
                            <small className="ur-field-hint">Must be senior to New Role</small>
                        </label>
                        <Select
                            className="w-100"
                            placeholder={newRoleId ? "Select Parent Role" : "Select New Role first"}
                            value={parentRoleId || undefined}
                            onChange={handleParentRoleChange}
                            disabled={!newRoleId || !parentRoleOptions.length}
                            loading={roleParentLoading}
                            showSearch
                            allowClear
                            size="large"
                            optionFilterProp="label"
                            options={parentRoleOptions.map(r => ({
                                value: r._id,
                                label: `${r.name} (${r.code})`
                            }))}
                        />
                        {newRoleId && !parentRoleOptions.length && !roleParentLoading && (
                            <small className="ur-error-hint">
                                <i className="fa fa-exclamation-circle mr-1"></i>
                                No eligible parent role for this selection.
                            </small>
                        )}
                    </div>

                    <div className="ur-field">
                        <label className="ur-field-label">
                            Parent User <span className="ur-required">*</span>
                        </label>
                        <Select
                            className="w-100"
                            placeholder={parentRoleId ? "Select Parent User" : "Select Parent Role first"}
                            value={parentUserId || undefined}
                            onChange={(v) => setParentUserId(v)}
                            disabled={!parentRoleId}
                            loading={roleParentLoading}
                            showSearch
                            allowClear
                            size="large"
                            optionFilterProp="label"
                            options={parentUserOptions.map(u => ({
                                value: u._id,
                                label: `${u.name} (${u.username})`
                            }))}
                        />
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default UpgradeUserRole;